// src/screens/CreatePollScreen.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './CreatePollScreen.css';

import { usePostContext } from '../context/PostContext';
import { useProfileContext } from '../context/ProfileContext';

import backIcon from '../assets/backspace-icon.png';
import placeholderProfile from '../assets/placeholder-profile.png';

export default function CreatePollScreen({ user }) {
  const navigate = useNavigate();
  const { addPost } = usePostContext();
  const { addEnrichedPost } = useProfileContext();

  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const trimmedQuestion = question.trim();

  const cleanedOptions = useMemo(() => {
    return options.map((opt) => opt.trim()).filter(Boolean);
  }, [options]);

  const isValid =
    trimmedQuestion.length >= 3 &&
    cleanedOptions.length >= 2 &&
    new Set(cleanedOptions.map((opt) => opt.toLowerCase())).size === cleanedOptions.length;

  const handleBack = () => {
    navigate(-1);
  };

  const handleOptionChange = (index, value) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddOption = () => {
    if (options.length >= 4) return;
    setOptions((prev) => [...prev, '']);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePublish = async () => {
    if (!isValid || submitting) return;

    try {
      setSubmitting(true);
      setError('');

      const payload = {
        username: user?.username,
        content: trimmedQuestion,
        postType: 'poll',
        pollQuestion: trimmedQuestion,
        pollOptions: cleanedOptions,
        pollDurationHours: expiresInHours,
      };

      console.log('📤 [CreatePoll] payload:', payload);

      const res = await api.post('/posts', payload);

      console.log('✅ [CreatePoll] success:', res.data);

      const createdPost = res.data;
      const postId = createdPost?._id || createdPost?.post?._id;

      const basePost = createdPost?.post || createdPost;

      const enriched = {
        ...basePost,
        _id: postId,
        postType: 'poll',
        type: 'poll',
        content: basePost?.content || trimmedQuestion,
        createdAt: basePost?.createdAt || new Date().toISOString(),
        timestamp: basePost?.createdAt || new Date().toISOString(),
        user: {
          name: user?.name || user?.fullName || user?.username,
          username: user?.username,
          profilePic: user?.profilePic,
          location: user?.location,

          userType: user?.userType,
          isMerchant: user?.isMerchant,
          merchantActive: user?.isMerchant === true,
          isAdmin: user?.isAdmin === true,
          adminRole: user?.adminRole || null,
          accountType: user?.accountType,
          isApp: user?.accountType === 'app',

          badges: Array.isArray(user?.badges) ? user.badges : [],
          badgesBrief: Array.isArray(user?.badges)
            ? user.badges.map((b) => b?.type).filter(Boolean)
            : [],
        },
        name: user?.name || user?.fullName || user?.username,
        username: user?.username,
        profilePic: user?.profilePic,
        location: user?.location,

        likes: Array.isArray(basePost?.likes) ? basePost.likes : [],
        comments: Array.isArray(basePost?.comments) ? basePost.comments : [],
        tips: Array.isArray(basePost?.tips) ? basePost.tips : [],
        savedBy: Array.isArray(basePost?.savedBy) ? basePost.savedBy : [],

        likesCount: Array.isArray(basePost?.likes)
          ? basePost.likes.length
          : Number(basePost?.likesCount || 0),
        commentsCount: Array.isArray(basePost?.comments)
          ? basePost.comments.length
          : Number(basePost?.commentsCount || 0),
        tipsCount: Array.isArray(basePost?.tips)
          ? basePost.tips.length
          : Number(basePost?.tipsCount || 0),

        userHasLiked: false,
        userHasSaved: false,

        mentionMap: basePost?.mentionMap || {},
        mentions: Array.isArray(basePost?.mentions) ? basePost.mentions : [],

        media: Array.isArray(basePost?.media) ? basePost.media : [],
        mediaType: basePost?.mediaType || '',

        poll: basePost?.poll || {
          question: trimmedQuestion,
          options: cleanedOptions.map((opt, index) => ({
            _id: `temp-${index}`,
            text: opt,
            votes: 0,
            percentage: 0,
          })),
          totalVotes: 0,
          expiresAt: new Date(
            Date.now() + expiresInHours * 60 * 60 * 1000
          ).toISOString(),
          isExpired: false,
        },
      };

      addPost(enriched);
      await addEnrichedPost(enriched, user?.username);

      navigate('/home');
    } catch (err) {
      console.error('❌ [CreatePoll] full error:', err);
      console.error('❌ [CreatePoll] response data:', err?.response?.data);
      console.error('❌ [CreatePoll] status:', err?.response?.status);

      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to publish poll.';

      setError(backendMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const displayAvatar = user?.profilePic || placeholderProfile;
  const displayName = user?.name || user?.fullName || user?.username || 'Pioneer';

  const isMerchant =
    user?.userType === 'merchant' &&
    (user?.isMerchant || user?.user?.isMerchant);

  const isAdmin = user?.isAdmin === true || !!user?.adminRole;
  const isApp = user?.accountType === 'app';

  const displayRole = isAdmin
    ? 'Admin'
    : isApp
    ? 'App'
    : isMerchant
    ? 'Merchant'
    : 'Pioneer';

  return (
    <div className="createpollscreen">
      <div className="createpollscreen-topbar">
        <button
          type="button"
          className="createpollscreen-backbtn"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={backIcon} alt="Back" className="createpollscreen-backicon" />
        </button>

        <h1 className="createpollscreen-title">Create Poll</h1>

        <div className="createpollscreen-topbar-spacer" />
      </div>

      <div className="createpollscreen-body">
        <div className="createpollscreen-author">
          <div className="createpollscreen-author-avatar-wrap">
            <img
              src={displayAvatar}
              alt={displayName}
              className="createpollscreen-author-avatar"
            />
          </div>

          <div className="createpollscreen-author-meta">
            <div className="createpollscreen-author-name">
              {displayName}
            </div>

            <div
              className={`createpollscreen-role-pill ${
                displayRole === 'Pioneer' ? 'is-pioneer' : ''
              }`}
            >
              {displayRole}
            </div>
          </div>
        </div>

        <div className="createpollscreen-card">
          <label className="createpollscreen-label" htmlFor="poll-question">
            Question
          </label>
          <textarea
            id="poll-question"
            className="createpollscreen-question"
            placeholder="Ask a short question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={120}
          />

          <div className="createpollscreen-charcount">
            {question.trim().length}/120
          </div>
        </div>

        <div className="createpollscreen-card">
          <div className="createpollscreen-label">Options</div>

          <div className="createpollscreen-options">
            {options.map((option, index) => (
              <div className="createpollscreen-option-row" key={index}>
                <input
                  type="text"
                  className="createpollscreen-option-input"
                  placeholder={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  maxLength={40}
                />

                {options.length > 2 && (
                  <button
                    type="button"
                    className="createpollscreen-remove-option"
                    onClick={() => handleRemoveOption(index)}
                    aria-label={`Remove option ${index + 1}`}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {options.length < 4 && (
            <button
              type="button"
              className="createpollscreen-add-option"
              onClick={handleAddOption}
            >
              + Add option
            </button>
          )}
        </div>

        <div className="createpollscreen-card">
          <div className="createpollscreen-label">Expiration</div>

          <div className="createpollscreen-expiry-row">
            {[24, 48, 72].map((hours) => (
              <button
                key={hours}
                type="button"
                className={`createpollscreen-expiry-btn ${
                  expiresInHours === hours ? 'is-active' : ''
                }`}
                onClick={() => setExpiresInHours(hours)}
              >
                {hours}h
              </button>
            ))}
          </div>
        </div>

        {error ? <div className="createpollscreen-error">{error}</div> : null}
      </div>

      <div className="createpollscreen-footer">
        <button
          type="button"
          className="createpollscreen-publish-btn"
          onClick={handlePublish}
          disabled={!isValid || submitting}
        >
          {submitting ? 'Publishing...' : 'Publish Poll'}
        </button>
      </div>
    </div>
  );
}
